import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { hideModal } from "../../../actions/actionCreators";
import withModal from "../withModal";
import MaterialModal from "../MaterialModal";
import { parseDate } from "../../../helpers/parseDate";
import { Tooltip, TextField, IconButton, Paper } from "@material-ui/core";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import h from "../../../helpers";
import formatFn from "date-fns/format";

type IScriptCodeResult = {
    FlowId: number | null;
    FlowName: string;
    FlowItemId: number | null;
    FlowItemName: string;
    FlowCompanyId: number | null;
    FlowCompanyName: string;
    Script: string;
};

type Props = {
    isOpen: boolean;
};

const ScriptNodeSearchModal: React.FC<Props> = ({ isOpen }: Props) => {
    const dispatch = useDispatch();

    // state
    const [filter, setFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [scriptCodeResults, setScriptCodeResults] = useState<Array<IScriptCodeResult>>([]);
    const [disabledButton, setDisabledButton] = useState(false);
    // props

    const hide = () => {
        setFilter("");
        setStartDate("");
        setEndDate("");
        setScriptCodeResults([]);
        dispatch(hideModal("SCRIPT_NODE_SEARCH"));
    };

    const startSearchData = () => {
        setScriptCodeResults([]);

        if (filter == "") {
            return;
        }
        const url =
            "/Scripts/SearchCode?" +
            h.serialize({
                filter,
                startDate: startDate == "" ? "" : formatFn(parseDate(startDate), "MM/dd/yyyy"),
                endDate: endDate == "" ? "" : formatFn(parseDate(endDate), "MM/dd/yyyy"),
            });
        setIsLoading(true);
        fetch(url, { credentials: "same-origin", method: "POST" })
            .then(h.checkStatus)
            .then(h.toJson)
            .then(data => {
                let flowitemList = data["flowitems"] as unknown as Array<IScriptCodeResult>;

                setScriptCodeResults(flowitemList);
                setIsLoading(false);
            })
            .catch(error => {
                setScriptCodeResults([]);
                setIsLoading(false);
                h.error("Error getting template", error);
            });
    };

    const downloadReportFile = () => {
        if (!disabledButton) {
            setDisabledButton(true);

            const tmpStartDate = startDate == "" ? "" : formatFn(parseDate(startDate), "MM/dd/yyyy");
            const tmpEndDate = endDate == "" ? "" : formatFn(parseDate(endDate), "MM/dd/yyyy");
            const url = `/Scripts/ExportSearchCode?filter=${filter}&startDate=${tmpStartDate}&endDate=${tmpEndDate}`;
            const buttonDownloadFileATag = document.createElement("a");
            buttonDownloadFileATag.setAttribute("href", url);
            buttonDownloadFileATag.click();
            setTimeout(() => {
                setDisabledButton(false);
            }, 4500);
        }
    };

    const tableHeader = [
        { title: "FlowId", field: "FlowId", width: "10%" },
        { title: "FlowItemId", field: "FlowItemId", width: "10%" },
        { title: "Company", field: "FlowCompanyName", width: "20%" },
        { title: "Script", field: "Script", width: "60%" },
    ];

    const theme = createTheme({
        palette: {
            primary: {
                main: "#406eb3",
            },
            secondary: {
                main: "#406eb3",
            },
        },
        typography: {
            fontSize: 18,
        },
    });

    const content = (
        <ThemeProvider theme={theme}>
            <MaterialTable
                style={{ margin: "0px 0px" }}
                title="Search Results"
                isLoading={isLoading}
                columns={tableHeader}
                data={scriptCodeResults}
                options={{
                    filtering: true,
                    grouping: false,
                    search: false,
                    rowStyle: {
                        fontSize: 12,
                        overflowWrap: "break-word",
                        verticalAlign: "top",
                    },
                    pageSize: 5,
                    pageSizeOptions: [5, 10, 15, 20, 25, 50],
                    padding: "default",
                    toolbarButtonAlignment: "left",
                    tableLayout: "fixed",
                }}
                actions={[
                    {
                        icon: "file_download",
                        tooltip: "Download",
                        isFreeAction: true,
                        disabled: isLoading || scriptCodeResults.length == 0,
                        onClick: () => downloadReportFile(),
                    },
                ]}
                components={{
                    Toolbar: props => (
                        <div>
                            <MTableToolbar {...props} />
                            <div style={{ padding: "0px 16px" }}>
                                Only the first 10,000 characters of the script are included, and you can only filter on
                                those characters.
                            </div>
                        </div>
                    ),
                }}
            />
        </ThemeProvider>
    );

    return (
        <MaterialModal
            id="script-node-modal-main"
            isOpen={isOpen}
            maxWidth="md"
            fullWidth={true}
            title="Script Node Search"
            cancelButtonText="Close"
            onPrimaryButtonRequest={hide}
            primaryButtonText="Close"
            disableEscape={true}
            onClose={hide}
        >
            <div style={{ margin: "0px 0px 10px 0px" }}>
                <div className="row">
                    <div className="col-sm-5">
                        <TextField
                            id="search_field_box"
                            label="Search"
                            type="text"
                            inputProps={{ maxLength: "64" }}
                            style={{ width: "100%" }}
                            value={filter}
                            placeholder={"Search table/DB/schema."}
                            onChange={e => {
                                setScriptCodeResults([]);
                                setFilter(e.target.value);
                            }}
                        />
                    </div>
                    <div className="col-sm-3">
                        <TextField
                            id="Seasonal_Begin_Date"
                            label="Begin Date"
                            type="date"
                            value={startDate}
                            onChange={e => {
                                setScriptCodeResults([]);
                                setStartDate(e.target.value);
                            }}
                            style={{ width: "100%" }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </div>
                    <div className="col-sm-3">
                        <TextField
                            id="Seasonal_End_Date"
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={e => {
                                setScriptCodeResults([]);
                                setEndDate(e.target.value);
                            }}
                            style={{ width: "100%" }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </div>
                    <div className="col-sm-1">
                        <Tooltip title="Search code/table/DB/schema">
                            <IconButton
                                aria-label="copy_tablekey_to_clipboard"
                                onClick={() => startSearchData()}
                                edge="end"
                            >
                                <i className={"material-icons material-icons-primary"}>search</i>
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
                {scriptCodeResults != null && (
                    <div style={{ marginTop: "20px", height: "550px" }}>
                        <Paper>{content}</Paper>
                    </div>
                )}
            </div>
        </MaterialModal>
    );
};

export default withModal(ScriptNodeSearchModal, "SCRIPT_NODE_SEARCH");
